/**
 * Cargar un usado — lo que pasa al apretar «Cargar el equipo».
 *
 * Vivía adentro de la pantalla de Catálogo. Sale acá porque Inventario hace
 * exactamente lo mismo, y dos copias de «crear la ficha y después ingresar la
 * unidad» terminan haciendo dos cosas distintas.
 *
 * SON DOS LLAMADAS, Y LA SEGUNDA PUEDE FALLAR
 * -------------------------------------------
 * Si la ficha se creó y el ingreso no, el teléfono existe en la lista pero no
 * en el stock. Reintentar el alta entera crearía una segunda ficha del mismo
 * equipo —y el IMEI ni siquiera la dejaría pasar—. Por eso la ficha creada se
 * recuerda y reintentar repite SOLO el ingreso, contra ella.
 */
import { useState } from "react";

import { mostrarError, mostrarExito } from "@/lib/errores";
import { useCrearProducto, useIngresoStock } from "@/lib/queries";
import { type Producto, type Rubro } from "@/lib/tipos";

import { aNuevoUsado, rubroDeUsado, type FormUsado } from "./logica";

export const MENSAJE_UNIDAD_PENDIENTE =
  "La ficha quedó creada, pero la unidad no entró al stock. Reintentá el ingreso: no se crea otra ficha.";

export interface CargaDeUsado {
  cargar: (form: FormUsado) => Promise<boolean>;
  reintentar: () => Promise<boolean>;
  pendiente: string | null;
  olvidar: () => void;
  guardando: boolean;
}

export function useCargarUsado(rubros: ReadonlyArray<Rubro>): CargaDeUsado {
  const crear = useCrearProducto();
  const ingresar = useIngresoStock();
  const [sinUnidad, setSinUnidad] = useState<Producto | null>(null);

  async function ingresarUnidad(ficha: Producto): Promise<boolean> {
    try {
      await ingresar.mutateAsync({
        producto_id: ficha.id,
        cantidad: 1,
        tipo: "COMPRA",
        actualiza_costo: false,
        referencia: "COMPRA USADO",
      });
    } catch (error) {
      setSinUnidad(ficha);
      mostrarError(error);
      return false;
    }
    setSinUnidad(null);
    mostrarExito(
      `${ficha.nombre} quedó cargado`,
      `Código ${ficha.codigo_interno} · 1 unidad en stock`,
    );
    return true;
  }

  async function cargar(form: FormUsado): Promise<boolean> {
    let ficha: Producto;
    try {
      // La familia la decide lo que ES el equipo, no la pantalla desde la que
      // se carga: un iPhone va a «Usados» y un Samsung a «Celulares».
      ficha = await crear.mutateAsync(aNuevoUsado(form, rubroDeUsado(form, rubros)));
    } catch (error) {
      mostrarError(error);
      return false;
    }
    // Un equipo del proveedor NO entra al stock: no está en el local. Queda en
    // cero con `a_pedido`, y el mostrador lo ofrece «a pedido».
    if (form.origen !== "local") {
      mostrarExito(
        `${ficha.nombre} quedó cargado`,
        `Código ${ficha.codigo_interno} · a pedido, sin stock propio`,
      );
      return true;
    }
    return ingresarUnidad(ficha);
  }

  async function reintentar(): Promise<boolean> {
    return sinUnidad ? ingresarUnidad(sinUnidad) : true;
  }

  return {
    cargar,
    reintentar,
    pendiente: sinUnidad ? MENSAJE_UNIDAD_PENDIENTE : null,
    olvidar: () => setSinUnidad(null),
    guardando: crear.isPending || ingresar.isPending,
  };
}
